import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MiniGantt } from '@/components/calculator/MiniGantt';
import { CURRENCIES } from '@/lib/calculationEngine';

const getCurrencySymbol = (est) => CURRENCIES.find(c => c.value === est?.config?.currency)?.symbol || '$';
const formatCost = (val, sym) => `${sym}${(val || 0).toLocaleString()}`;

function CompareRow({ id, label, valA, valB, unit }) {
  const a = typeof valA === 'number' ? valA : 0;
  const b = typeof valB === 'number' ? valB : 0;
  const equal = a === b;
  const fmt = (v) => (unit && typeof v === 'number' ? `${v} ${unit}` : v);

  return (
    <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 py-2.5 border-b border-[#DDE1E5] last:border-0 items-center">
      <span className="text-xs text-[#66707A] font-medium">{label}</span>
      <span
        className={`text-sm font-mono text-right ${!equal && a <= b ? 'text-[#527F19]' : 'text-[#171A1D]'}`}
        data-testid={`compare-a-${id}`}
      >
        {fmt(valA)}
      </span>
      <span
        className={`text-sm font-mono text-right ${!equal && b <= a ? 'text-[#527F19]' : 'text-[#171A1D]'}`}
        data-testid={`compare-b-${id}`}
      >
        {fmt(valB)}
      </span>
    </div>
  );
}

function SectionLabel({ children, className = '' }) {
  return (
    <div className={`text-xs text-[#66707A] uppercase tracking-wider mb-2 ${className}`}>{children}</div>
  );
}

/** Side-by-side comparison dialog for two saved estimates. */
export function CompareEstimatesDialog({ open, onOpenChange, estimates }) {
  const { t } = useTranslation();
  const industryOf = (est) => (est?.config?.industry ? t(`calcData.industries.${est.config.industry}`) : '-');
  const sizeOf = (est) => (est?.config?.companySize ? t(`calcData.sizes.${est.config.companySize}`) : '-');
  const approachOf = (est) => (est?.config?.approach ? t(`approach.${est.config.approach}`) : '-');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-[#DDE1E5] text-[#171A1D] max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="compare-dialog"
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="font-display font-semibold tracking-tight text-lg">
            {t('estimates.compareTitle')}
          </DialogTitle>
        </DialogHeader>
        {estimates.length === 2 && (() => {
          const [a, b] = estimates;
          const symA = getCurrencySymbol(a);
          const symB = getCurrencySymbol(b);
          return (
            <div className="mt-2 space-y-5">
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-3 pb-3 border-b border-[#DDE1E5]">
                <span className="text-xs text-[#66707A] uppercase tracking-wider">{t('estimates.metric')}</span>
                <div className="text-right">
                  <div className="text-sm font-bold text-[#527F19] truncate" data-testid="compare-name-a">{a.name}</div>
                  <div className="text-xs text-[#66707A]">{industryOf(a)}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-[#66707A] truncate" data-testid="compare-name-b">{b.name}</div>
                  <div className="text-xs text-[#66707A]">{industryOf(b)}</div>
                </div>
              </div>

              <div className="space-y-0">
                <SectionLabel>{t('estimates.coreMetrics')}</SectionLabel>
                <CompareRow id="total-effort" label={t('estimates.totalEffort')} unit={t('estimates.days')}
                            valA={a.results?.totalEffort} valB={b.results?.totalEffort} />
                <CompareRow id="duration" label={t('estimates.duration')} unit={t('estimates.weeks')}
                            valA={a.results?.totalDurationWeeks} valB={b.results?.totalDurationWeeks} />
                <CompareRow id="fte" label={t('estimates.fteRequired')} unit={t('estimates.fteUnit')}
                            valA={a.results?.fteRequired} valB={b.results?.fteRequired} />
                <CompareRow id="cost-low" label={t('estimates.costLow')}
                            valA={formatCost(a.results?.costRange?.low, symA)} valB={formatCost(b.results?.costRange?.low, symB)} />
                <CompareRow id="cost-high" label={t('estimates.costHigh')}
                            valA={formatCost(a.results?.costRange?.high, symA)} valB={formatCost(b.results?.costRange?.high, symB)} />

                <SectionLabel className="mt-4">{t('estimates.resourceSplit')}</SectionLabel>
                <CompareRow id="internal-days" label={t('estimates.internalDays')} unit={t('estimates.days')}
                            valA={a.results?.internalDays} valB={b.results?.internalDays} />
                <CompareRow id="consultant-days" label={t('estimates.consultantDays')} unit={t('estimates.days')}
                            valA={a.results?.consultantDays} valB={b.results?.consultantDays} />

                <SectionLabel className="mt-4">{t('estimates.configuration')}</SectionLabel>
                <CompareRow id="company-size" label={t('companyProfile.companySize')} valA={sizeOf(a)} valB={sizeOf(b)} />
                <CompareRow id="approach" label={t('approach.label')} valA={approachOf(a)} valB={approachOf(b)} />
              </div>

              <div>
                <SectionLabel className="mb-3">{t('estimates.timelineComparison')}</SectionLabel>
                <div
                  className="space-y-5 bg-[#F5F6F7] rounded-lg p-4 border border-[#DDE1E5]"
                  data-testid="compare-gantt-section"
                >
                  <MiniGantt results={a.results} label={a.name} color="#78B928" />
                  <div className="border-t border-[#DDE1E5]" />
                  <MiniGantt results={b.results} label={b.name} color="#66707A" />
                </div>
              </div>
            </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
